
// Unique identifier for this webview
// If multiple webviews are displayed at the same time,
// the browser message API propagate the event to all webview

import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { WebViewSharedProps, WebViewSource, WebViewMessageEvent } from 'react-native-webview/lib/WebViewTypes';

import type { ForwardRefExoticComponent, RefAttributes } from 'react';

import PlatformWebView from './Platform/index';
import injectedJavaScript from './injectedScript';

import { getAuth } from '@lib/service';
import { uniqueId } from 'underscore';

import { _print, _share } from './utils';

const configureStyles = ({width, height, fullpage, ...props}: any) => {

  return StyleSheet.create({

    wrapper: {
      display: 'flex',
      overflow: 'hidden',

      width: fullpage ? '100%' : (width || undefined),
      height: fullpage ? '100%' : (height || undefined),
    },

  });

};

type Props = WebViewSharedProps & {
    source: WebViewSource;
    onMessage?: (event: WebViewMessageEvent) => void;
    fullpage?: boolean;
};


export const WebView: ForwardRefExoticComponent<Props & RefAttributes<any>> = React.forwardRef<any, Props>(
  ({ onMessage, fullpage = false, ...props }, ref) => {
    const webviewRef = React.useRef(null);
    const [uniqueID,] = useState(uniqueId());
    const [pdfDataURI, setPdfDataURI] = React.useState(null);

    const [width,] = useState('100%');
    const [height, setHeight] = useState(0);

    const [styles, setStyles] = useState<any>(undefined);

    // The authenticate (bearer or cookie) to appy to the webview
    const [token, setToken] = useState<null | string>(null);

    React.useImperativeHandle(ref, () => ({
      // keep default attributes
      ...(webviewRef.current ? webviewRef.current : {}),
      // custom function
      // @ts-ignore
      focus: () => webviewRef.current.focus(),
      // @ts-ignore
      print: () =>_print(props.source.html),
      // @ts-ignore
      share: () => _share(props.source.html, pdfDataURI),
    }));

    // This is invoiked *after* every render
    React.useEffect(() => {
      // @ts-ignore
      if (ref?.current) {
          console.log("ref.current is initialized")
      }
    });

    React.useEffect( () => {
      const styles = configureStyles({width, height, fullpage}) as unknown;
      setStyles(styles);
    }, [width, height, fullpage]);

    // Load auth token before we render the webview
    React.useEffect(() => {
          const load = async () => {
              const auth = await getAuth();
              const token = auth?.token ? auth.token : '';
              setToken(token);
          };
        load();
    }, []);

    // Message handler that filter message by webview ID
    const _onMessage = (event: WebViewMessageEvent) => {
        try {
            // Parse the JSON string
            const json = JSON.parse(event.nativeEvent.data);
            // Extract data / props from the webview
            const data = json[uniqueID];

            // // TODO: A unique ID per web view is provided only for locally loaded
            // //       HTML page.
            // //      Please support unique ID universally.
            // //      Otherwise, messages a propagated to all web view.
            // // Process data / props
            // if (data) {
            //   // Send the webview native message to the parent component
            //   onMessage ? onMessage(event) : undefined;
            // }

            // Update the height of the webview
            data?.height ? setHeight(data.height) : undefined;
            // PDF
            // use internal hook
            (data?.pdfDataURI && setPdfDataURI) ? setPdfDataURI(data.pdfDataURI) : undefined;

            onMessage ? onMessage(event) : undefined;
        } catch (error) {
            console.log(error);
        }
    };


    if (!token) return null;

    return (
      <View style = { styles?.wrapper }>
          <PlatformWebView
            token={token}
            onMessage={_onMessage}
            injectedJavaScript={injectedJavaScript(uniqueID) as unknown as string}
            {...props}

            ref={webviewRef}
          />
      </View>);
});

export default WebView;



